// 4.22-4.23 Send by hand. Firms held for manual outreach only (2.37: most are sole traders, or in a country the
// business doesn't send to automatically) still get a message drafted, but Ogma never sends it. Each card shows the
// firm, the channel, a one-tap link to their Instagram or Facebook profile (or the email filled in, or the number to
// ring), Copy, the warning, Edit, Skip and Sent ("I've sent it"), which logs the touch through act. Shown on Prospects › Drafts ›
// Send by hand, and in the approval queue (task kind send_by_hand).
// J-03 (18 Oct 2026), two clicks a message: one primary button copies the message and opens the channel in one go
// ("Copy and open Instagram", Facebook, email: a Gmail or Outlook compose window, filled in, when the mailbox is
// known; "Copy and call" for a call), then "I've sent it". The warning is shown once at the top of the list, and Edit,
// Skip, "Sent it another way?" and Why this firm are quiet links under the card.
import { useId, useMemo, useState } from "react";
import { Camera, Copy, ExternalLink, Mail, MessageSquare, Phone, TriangleAlert, UserPlus, type LucideIcon } from "lucide-react";
import { mailboxState } from "../../lib/mailboxHealth";
import type { Crm } from "../../lib/crm";
import type { HandSend, HandSendChannel, HumanTask, ReviewDraft } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { fmtDate, humanise } from "../../lib/format";
import { Async, Empty, Notice } from "../../components/ui";
import { CONFIDENCE_LABEL, failedChecks, researchOf } from "../../lib/review";
import {
  CHANNEL_LABEL,
  HAND_SEND_WARNING,
  contactLine,
  sentNote,
  shortReason,
  HOLD_LABEL,
  copyText,
  handSendSummary,
  languageName,
  mailService,
  openLink,
  otherChannels,
  type MailService,
} from "../../lib/handSend";
import "../find/find-review.css";
import "./send-by-hand.css";

/** Approvals.tsx TASKS_CHANGED (not imported: Approvals imports this file). Tells the menu its counts changed. */
const TASKS_CHANGED = "ogma:tasks-changed";

export const CHANNEL_ICON: Record<HandSendChannel, LucideIcon> = { instagram: Camera, facebook: MessageSquare, email: Mail, call: Phone, linkedin: UserPlus };

type Outcome = { kind: "sent"; channel: HandSendChannel; next?: string } | { kind: "skipped" };

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Why this firm: the research card the draft was written from (the same contract as 4.2). */
function WhyThisFirm({ h }: { h: HandSend }) {
  const card = useMemo(() => researchOf({ research: (h.metadata as Record<string, unknown>).research } as unknown as ReviewDraft), [h.metadata]);
  const failed = failedChecks(card);
  const meta = h.metadata as Record<string, unknown>;
  const language = languageName(h.language);
  return (
    // card-check: not a card (a show-more of the research behind the draft, inside its row)
    <details className="disclosure">
      <summary>
        Why this firm{card?.confidence ? ` · ${CONFIDENCE_LABEL[card.confidence].toLowerCase()}` : ""}
        {failed.length ? ` · ${failed.length} ${failed.length === 1 ? "check" : "checks"} to look at` : ""}
      </summary>
      {failed.length > 0 && (
        <ul className="research-failed" aria-label="Checks to look at">
          {failed.map((c) => (
            <li key={c.rule}>
              <strong>{humanise(c.rule)}</strong>
              {c.detail ? `: ${c.detail}` : ""}
            </li>
          ))}
        </ul>
      )}
      <dl className="research-facts">
        {card?.why_now && (
          <>
            <dt>Why now</dt>
            <dd>{card.why_now}</dd>
          </>
        )}
        {card?.hook && (
          <>
            <dt>Hook</dt>
            <dd>{card.hook}</dd>
          </>
        )}
        {typeof meta.channel_reason === "string" && (
          <>
            <dt>Channel</dt>
            <dd>{meta.channel_reason}</dd>
          </>
        )}
        {language && (
          <>
            <dt>Language</dt>
            <dd>
              {language}
              {typeof meta.language_reason === "string" ? ` (${meta.language_reason})` : ""}
            </dd>
          </>
        )}
        <dt>Held because</dt>
        <dd>{HOLD_LABEL[h.hold_reason] ?? humanise(h.hold_reason)}</dd>
      </dl>
      {card && card.sources.length > 0 && (
        <p className="research-sources small">
          <span className="muted">Sources: </span>
          {card.sources.map((s, i) => (
            <span key={`${s.label}-${i}`}>
              {i > 0 ? ", " : ""}
              {s.url ? (
                <a href={s.url} target="_blank" rel="noreferrer noopener">
                  {s.label}
                </a>
              ) : (
                <span className="muted">{s.label}</span>
              )}
            </span>
          ))}
        </p>
      )}
    </details>
  );
}

/** The mail service of the business's connected mailbox (Gmail or Outlook), for the compose window; null if unknown. */
export function useMailService(crm: Crm): MailService {
  const health = useAsync(() => (typeof crm.mailboxHealth === "function" ? crm.mailboxHealth().catch(() => null) : Promise.resolve(null)), [crm]);
  return mailService(mailboxState(health.data ?? null).provider);
}

export function HandSendCard({ crm, h, primary, service = null, onChanged }: { crm: Crm; h: HandSend; primary: boolean; service?: MailService; onChanged: () => void }) {
  const id = useId();
  const [subject, setSubject] = useState(h.subject ?? "");
  const [body, setBody] = useState(h.body);
  const [editing, setEditing] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [opened, setOpened] = useState(false);
  const [otherWay, setOtherWay] = useState(false);
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const edited = body !== h.body || subject !== (h.subject ?? "");
  const link = openLink(h, body, subject, service);
  const Icon = CHANNEL_ICON[h.channel];
  const others = otherChannels(h);
  const where = [h.city, h.country].filter(Boolean).join(", ");
  const why = shortReason(h);
  const contact = contactLine(h);

  /** `decides`: the message is sent or skipped (not just edited), so it leaves the list and the counts change. */
  const run = async (fn: () => Promise<unknown>, after: (r: unknown) => void, decides = true) => {
    setBusy(true);
    setMsg(null);
    try {
      after(await fn());
      if (!decides) return;
      window.dispatchEvent(new Event(TASKS_CHANGED));
      onChanged();
    } catch (e) {
      setMsg({ text: errorText(e), tone: "bad" });
    } finally {
      setBusy(false);
    }
  };

  const sent = (channel?: HandSendChannel) =>
    void run(
      () => crm.markHandSent(h.id, { ...(channel && channel !== h.channel ? { channel } : {}), ...(edited ? { body, ...(h.channel === "email" ? { subject } : {}) } : {}) }),
      (r) => setOutcome({ kind: "sent", channel: (r as { channel?: HandSendChannel } | null)?.channel ?? channel ?? h.channel, next: (r as { next_follow_up_at?: string } | null)?.next_follow_up_at }),
    );

  const copy = async () => {
    setOpened(true);
    await navigator.clipboard?.writeText(copyText(h, body, subject)).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  // The first message's main button leads; once it is opened (or copied), "I've sent it" leads instead.
  const openLeads = primary && !opened;
  const sentLeads = primary && opened;

  if (outcome) {
    return (
      <li className="hand-send is-done" data-hand-send={h.id}>
        <p className="small" role="status">
          <strong>{h.organisation_name}</strong>:{" "}
          {outcome.kind === "sent"
            ? `logged as sent by ${CHANNEL_LABEL[outcome.channel].toLowerCase()}.${outcome.next ? ` A follow-up will be drafted after ${fmtDate(outcome.next)} if they haven't replied.` : ""}`
            : "skipped. Ogma won't draft anything else for this firm."}
        </p>
      </li>
    );
  }

  return (
    <li className={`hand-send review-card${primary ? " is-current" : ""}`} data-hand-send={h.id}>
      <div className="review-draft">
        <div className="draft-head">
          <span id={`${id}-to`} className="draft-to">
            <strong>{h.organisation_name}</strong>
            {h.full_name && <span className="muted small">{h.full_name}</span>}
            {where && <span className="muted small">{where}</span>}
          </span>
          <span className="muted small hand-send-channel" title={`Step ${h.step_index ? "4.22, follow-up" : "4.22"}`}>
            <Icon size={16} strokeWidth={1.75} aria-hidden="true" focusable="false" /> {CHANNEL_LABEL[h.channel]}
            {h.step_index > 0 ? " · follow-up" : ""}
            {h.language && h.language !== "en" ? ` · in ${languageName(h.language)}` : ""}
            {edited ? <strong className="edited"> Edited</strong> : null}
          </span>
        </div>
        {editing ? (
          <>
            {h.channel === "email" && (
              <label className="field" htmlFor={`${id}-subject`}>
                <span className="field-label">Subject</span>
                <input id={`${id}-subject`} value={subject} maxLength={300} onChange={(e) => setSubject(e.target.value)} />
              </label>
            )}
            <label className="field" htmlFor={`${id}-body`}>
              <span className="field-label">Message</span>
              <textarea id={`${id}-body`} className="autogrow" rows={Math.max(4, body.split("\n").length + 1)} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>
          </>
        ) : (
          <div className="review-message">
            {h.channel === "email" && <p className="review-subject">{subject || <span className="muted">(no subject)</span>}</p>}
            <div className="review-body">{body}</div>
          </div>
        )}
        {contact && <p className="small muted hand-send-contact">{contact}</p>}
        {why && (
          <p className="small hand-send-reason">
            <span className="muted">Why: </span>
            {why}
          </p>
        )}
        {skipping ? (
          <div className="reject-reasons" role="group" aria-label={`Skip ${h.organisation_name}?`}>
            <label className="field" htmlFor={`${id}-reason`}>
              <span className="field-label">Why (optional)</span>
              <input id={`${id}-reason`} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
            </label>
            <div className="fold-actions">
              <button type="button" className="btn" disabled={busy} onClick={() => void run(() => crm.skipHandSend(h.id, reason.trim() || undefined), () => setOutcome({ kind: "skipped" }))}>
                Skip this firm
              </button>
              <button type="button" className="btn btn-small btn-quiet" onClick={() => setSkipping(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="review-actions hand-send-links" role="group" aria-labelledby={`${id}-to`}>
              {link ? (
                <a
                  className={`btn${openLeads ? " btn-primary" : ""}`}
                  href={link.href}
                  onClick={() => void copy()}
                  {...(link.external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
                >
                  {link.external ? <ExternalLink size={14} strokeWidth={1.75} aria-hidden="true" focusable="false" /> : <Icon size={14} strokeWidth={1.75} aria-hidden="true" focusable="false" />}{" "}
                  {copied ? "Copied: paste it in" : link.label}
                </a>
              ) : (
                <button type="button" className={`btn${openLeads ? " btn-primary" : ""}`} onClick={() => void copy()} aria-label={`Copy the message to ${h.organisation_name}`}>
                  <Copy size={14} strokeWidth={1.75} aria-hidden="true" focusable="false" /> {copied ? "Copied" : "Copy message"}
                </button>
              )}
              <button type="button" className={`btn${sentLeads ? " btn-primary" : ""}`} disabled={busy} onClick={() => sent()}>
                {busy ? "Saving…" : "Sent"}
              </button>
            </div>
            <p className="small muted hand-send-sent-note">{sentNote(h)}</p>
            <div className="hand-send-quiet">
              {editing ? (
                <button
                  type="button"
                  className="link-btn"
                  disabled={busy || !body.trim()}
                  onClick={() =>
                    edited
                      ? void run(
                          () => crm.editHandSend(h.id, body, h.channel === "email" ? subject : undefined),
                          () => {
                            setEditing(false);
                            setMsg({ text: "Saved.", tone: "ok" });
                          },
                          false,
                        )
                      : setEditing(false)
                  }
                >
                  {edited ? "Save changes" : "Done editing"}
                </button>
              ) : (
                <button type="button" className="link-btn" disabled={busy} onClick={() => setEditing(true)}>
                  Edit
                </button>
              )}
              {link && (
                <button type="button" className="link-btn" onClick={() => void copy()} aria-label={`Copy the message to ${h.organisation_name}`}>
                  {copied ? "Copied" : "Copy only"}
                </button>
              )}
              <button type="button" className="link-btn" disabled={busy} onClick={() => setSkipping(true)}>
                Skip
              </button>
              {others.length > 0 && (
                <button type="button" className="link-btn" aria-expanded={otherWay} onClick={() => setOtherWay(!otherWay)}>
                  Sent it another way?
                </button>
              )}
            </div>
            {otherWay && others.length > 0 && (
              <div className="fold-actions">
                {others.map((c) => (
                  <button key={c} type="button" className="btn btn-small" disabled={busy} onClick={() => sent(c)}>
                    I sent it by {CHANNEL_LABEL[c].toLowerCase()}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <WhyThisFirm h={h} />
        <Notice text={msg?.text ?? null} tone={msg?.tone} />
      </div>
    </li>
  );
}

/** Every message waiting to be sent by hand. `onEmpty` runs once nothing is left (the approval card closes). */
export function SendByHandList({ crm, onEmpty }: { crm: Crm; onEmpty?: () => void }) {
  const list = useAsync(() => crm.handSends(), [crm]);
  return (
    <Async
      state={list}
      label="Loading messages to send by hand"
      empty={(d) =>
        d.length === 0 ? (
          <Empty title="Nothing to send by hand">
            <p>Messages for firms Ogma can't contact automatically (most sole traders, and countries you haven't cleared) appear here for you to send yourself.</p>
          </Empty>
        ) : null
      }
    >
      {(d) => <HandSendItems crm={crm} items={d} onEmpty={onEmpty} />}
    </Async>
  );
}

/** The cards. Each stays in place once decided, marked, so the list doesn't jump; `onEmpty` when all are decided. */
export function HandSendItems({ crm, items, onEmpty, warning = true, lead = true, summary = true }: { crm: Crm; items: HandSend[]; onEmpty?: () => void; warning?: boolean; lead?: boolean; summary?: boolean }) {
  const [finished, setFinished] = useState<string[]>([]);
  const service = useMailService(crm);
  // `lead`: this list holds the page's one main action (its first waiting message). Off for the later campaign groups.
  const first = lead ? items.find((x) => !finished.includes(x.id))?.id : undefined;
  return (
    <>
      {warning && <HandSendWarning />}
      {summary && <p className="small muted">{handSendSummary(items)}. Copy and open each one, send it, then tap Sent so Ogma logs it and drafts the follow-up.</p>}
      <ul className="review-cards hand-sends">
        {items.map((h) => (
          <HandSendCard
            key={h.id}
            crm={crm}
            h={h}
            primary={h.id === first}
            service={service}
            onChanged={() =>
              setFinished((was) => {
                const next = was.includes(h.id) ? was : [...was, h.id];
                if (next.length >= items.length) window.setTimeout(() => onEmpty?.(), 0);
                return next;
              })
            }
          />
        ))}
      </ul>
    </>
  );
}

/** The warning, once for the whole list (it used to sit on every card). */
export function HandSendWarning() {
  return (
    <p className="notice notice-warn hand-send-warning hand-send-page-warning" role="note">
      <TriangleAlert size={16} strokeWidth={1.75} aria-hidden="true" focusable="false" /> {HAND_SEND_WARNING}
    </p>
  );
}

/** The approval queue's card for the send_by_hand task (4.23). */
export function SendByHandTask({ crm, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  return <SendByHandList crm={crm} onEmpty={onDone} />;
}
